import { ITokens } from "@/types/common";
import { fetchApi } from "@/modules/server";

export const USE_GRAPHHOPPER = true;
const BASE = "https://routing-service.loadconnect.io";

type Pt = { lat: number; lng: number };
type Stop = { point: Pt; name: string; state?: string; countrycode?: string; id: string };
type Leg = { coords: Pt[]; meters: number; ms: number };
type PricePt = { lat: number; lng: number; price: number; name: string };
type Tolls = {
  tolls: PricePt[];
  total: number;
  fuel: Record<string, any>;
  fuels: PricePt[];
};

const wkt = (raw: unknown): Pt | null => {
  const m = String(raw || "").match(/POINT\s*\(\s*([-\d.]+)\s+([-\d.]+)\s*\)/i);
  return m ? { lng: +m[1], lat: +m[2] } : null;
};

const parseStations = (raw: unknown): PricePt[] =>
  (Array.isArray(raw) ? raw : []).flatMap((s: any) => {
    const lat = Number(s?.lat);
    const lng = Number(s?.lng);
    const price = Number(s?.pricePerGallon ?? s?.diesel ?? s?.price);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(price))
      return [];
    return [{ lat, lng, price, name: s.brand || s.city || s.name || "" }];
  });

const fuelOnStops = (fuel: Record<string, any>, points: Stop[]): PricePt[] =>
  Object.entries(fuel || {}).flatMap(([city, p]) => {
    const price = Number(p?.diesel);
    if (!Number.isFinite(price)) return [];
    if (Number.isFinite(p?.lat) && Number.isFinite(p?.lng)) {
      return [{ lat: +p.lat, lng: +p.lng, price, name: city }];
    }
    const q = city.toLowerCase();
    const hit = points.find((pt) => {
      const n = (pt.name || "").toLowerCase();
      return n && (q.includes(n) || n.includes(q.split(",")[0]));
    });
    return hit ? [{ lat: hit.point.lat, lng: hit.point.lng, price, name: city }] : [];
  });

const json = async (path: string, tokens: ITokens, body?: unknown) => {
  const res = await fetchApi(
    BASE + path,
    {
      method: body ? "POST" : "GET",
      headers: {
        Authorization: `Bearer ${tokens.accessToken}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    },
    tokens.accessToken,
    tokens.refreshToken
  );
  if (!res.ok) throw Object.assign(new Error(res.statusText), { status: res.status });
  const data = await res.json();
  return data?.content ?? data;
};

export const decodePolyline = (encoded = "", mul = 1e5): Pt[] => {
  const out: Pt[] = [];
  let i = 0, lat = 0, lng = 0;
  while (i < encoded.length) {
    let b, shift = 0, result = 0;
    do {
      b = encoded.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lat += result & 1 ? ~(result >> 1) : result >> 1;
    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(i++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    lng += result & 1 ? ~(result >> 1) : result >> 1;
    out.push({ lat: lat / mul, lng: lng / mul });
  }
  return out;
};

const geocode = async (text: string, id: string, tokens: ITokens): Promise<Stop | null> => {
  const q = text.trim();
  if (!q) return null;
  try {
    const content = await json(
      `/api/geocoder/v1/autocomplete/graphhopper?text=${encodeURIComponent(q)}`,
      tokens
    );
    const hit =
      content?.hits?.find((h: any) => h.osm_key === "place") || content?.hits?.[0];
    if (hit?.point) {
      return { point: hit.point, name: hit.name, state: hit.state, countrycode: hit.countrycode, id };
    }
  } catch {}
  return null;
};

const parseTolls = (content: any): Tolls => {
  const raw = content?.toll || {};
  const tolls: PricePt[] = [];
  let total = 0;
  let sum = 0;
  Object.entries(raw).forEach(([key, value]: [string, any]) => {
    if (key === "total") {
      total = Number(value) || 0;
      return;
    }
    const loc = value?.locations?.[0];
    const pt = wkt(value?.originPoint) ||
      (Number.isFinite(loc?.lat) && Number.isFinite(loc?.lng)
        ? { lat: +loc.lat, lng: +loc.lng }
        : null);
    const price = Number(value?.price ?? value?.fares?.[0]?.amount) || 0;
    sum += price;
    if (!pt || price <= 0) return;
    tolls.push({
      ...pt,
      price,
      name: value?.name || loc?.name || "",
    });
  });
  return {
    tolls,
    total: total || sum,
    fuel: content?.fuel || {},
    fuels: parseStations(content?.stations || content?.fuelStations),
  };
};

type GhRoute = { points: Stop[]; legs: Leg[]; sid: string };
const routeCache = new Map<string, { at: number; route: GhRoute }>();
const ROUTE_TTL = 60 * 60 * 1000;

export const fetchGhRoute = async (stops: string[], tokens: ITokens) => {
  const list = stops.filter(Boolean);
  if (list.length < 2 || !tokens.accessToken) return null;
  const cached = routeCache.get(list.join("|"));
  if (cached && Date.now() - cached.at < ROUTE_TTL) return cached.route;

  const points = await Promise.all(list.map((s, i) => geocode(s, String(i), tokens)));
  if (points.some((p) => !p)) return null;

  try {
    const route = await json("/api/route/v1/points/graphhopper", tokens, { points });
    if (!route?.sid || !route.points) return null;

    const all = decodePolyline(route.points, route.points_encoded_multiplier || 1e5);
    const distances = route.details?.leg_distance || [];
    const times = route.details?.leg_time || [];
    const legs: Leg[] = distances.length
      ? distances.map((d: number[], i: number) => ({
          coords: all.slice(d[0], d[1] + 1),
          meters: d[2],
          ms: times[i]?.[2] || 0,
        }))
      : [{ coords: all, meters: route.distance, ms: route.time }];

    const result = { points: points as Stop[], legs, sid: route.sid };
    routeCache.set(list.join("|"), { at: Date.now(), route: result });
    return result;
  } catch {
    return null;
  }
};

export const pollTolls = async (sid: string, tokens: ITokens): Promise<Tolls> => {
  const empty: Tolls = { tolls: [], total: 0, fuel: {}, fuels: [] };
  await new Promise((r) => setTimeout(r, 1000));
  for (let i = 0; i < 6; i++) {
    try {
      const parsed = parseTolls(await json(`/api/toll/v1/cache/${sid}`, tokens));
      if (parsed.tolls.length || parsed.total || Object.keys(parsed.fuel).length)
        return parsed;
    } catch (e: any) {
      if (e?.status && ![202, 204, 404].includes(e.status)) break;
    }
    await new Promise((r) => setTimeout(r, 1200));
  }
  return empty;
};

export const toRouting8 = (gh: { points: Stop[]; legs: Leg[] }, tolls?: Tolls) => {
  const list = tolls?.tolls || [];
  const near = (p: Pt) =>
    gh.legs.reduce((best, l, idx) => {
      const d = Math.min(
        ...l.coords.map((c) => (c.lat - p.lat) ** 2 + (c.lng - p.lng) ** 2)
      );
      return d < best.d ? { i: idx, d } : best;
    }, { i: 0, d: Infinity }).i;
  const byLeg: Record<number, Tolls["tolls"]> = {};
  list.forEach((t) => {
    const i = near({ lat: t.lat, lng: t.lng });
    (byLeg[i] ||= []).push(t);
  });

  const sections = gh.legs.map((leg, i) => {
    const start = gh.points[i]?.point || leg.coords[0];
    const end = gh.points[i + 1]?.point || leg.coords[leg.coords.length - 1];
    const legTolls = byLeg[i] || [];
    return {
      coords: leg.coords,
      polyline: "",
      departure: { place: { location: start } },
      arrival: { place: { location: end } },
      travelSummary: {
        length: leg.meters,
        duration: leg.ms / 1000,
        tolls: { total: { value: legTolls.reduce((s, t) => s + t.price, 0) } },
      },
      tolls: legTolls.map((t) => ({
        price: t.price,
        tollCollectionLocations: [{ location: { lat: t.lat, lng: t.lng }, name: t.name }],
      })),
    };
  });
  if (!list.length && tolls?.total && sections.length) {
    sections[sections.length - 1].travelSummary.tolls.total.value = tolls.total;
  }
  return {
    routes: [{ sections }],
    _fuel: tolls?.fuel || {},
    _fuels: tolls?.fuels?.length ? tolls.fuels : fuelOnStops(tolls?.fuel || {}, gh.points),
  };
};

export const dieselFromFuel = (fuel: Record<string, any>, origin: string) => {
  const keys = Object.keys(fuel || {});
  if (!keys.length) return null;
  const city = (origin.split(",")[0] || "").trim().toLowerCase();
  const key = keys.find((k) => k.toLowerCase().includes(city)) || keys[0];
  const raw = fuel[key]?.diesel;
  if (raw == null || raw === "") return null;
  const diesel = Number(raw);
  if (!Number.isFinite(diesel) || diesel <= 0) return null;
  const cad = String(fuel[key]?.currency || "").toUpperCase() === "CAD";
  return {
    gallon: (cad ? diesel * 3.785 : diesel).toFixed(2),
    liter: (cad ? diesel : diesel / 3.785).toFixed(2),
  };
};
